import React, { useState, useEffect } from 'react'
import { supabase } from '../../../lib/supabase'
import { useAuth } from '../../../hooks/useAuth'
import { CheckCircle, Circle, Trash2, Plus, User, Clock, AlertCircle, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react'

interface Task {
  id: string
  task: string
  completed: boolean
  scheduled_at?: string
  created_at: string
  created_by: string
  created_by_name: string
  assigned_to?: string
  assigned_to_name?: string
  completed_at?: string
  priority?: string
}

interface TeamLeader {
  id: string
  name: string
  role: string
}

export const TaskManagerModule: React.FC = () => {
  const { user } = useAuth()
  const [tasks, setTasks] = useState<Task[]>([])
  const [teamLeaders, setTeamLeaders] = useState<TeamLeader[]>([])
  const [loading, setLoading] = useState(true)
  const [showAddForm, setShowAddForm] = useState(false)
  const [filterUser, setFilterUser] = useState<string>('')
  const [filterStatus, setFilterStatus] = useState<string>('')
  const [expandedTasks, setExpandedTasks] = useState<Set<string>>(new Set())

  // New task form state
  const [newTask, setNewTask] = useState('')
  const [assignedTo, setAssignedTo] = useState('')
  const [scheduledTime, setScheduledTime] = useState('')
  const [priority, setPriority] = useState('normaal')

  useEffect(() => {
    loadTeamLeaders()
    loadTasks()

    // Set up realtime subscription for live updates
    const subscription = supabase
      .channel('tasks-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tasks' }, () => {
        loadTasks()
      })
      .subscribe()

    return () => {
      subscription.unsubscribe()
    }
  }, [])

  const loadTeamLeaders = async () => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('id, name, role')
        .in('role', ['teamleader', 'admin', 'forklift', 'technical'])
        .order('name')

      if (error) {
        console.error('Error loading team leaders:', error)
        return
      }

      setTeamLeaders(data || [])
    } catch (error) {
      console.error('Error loading team leaders:', error)
    }
  }

  const loadTasks = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('tasks')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) {
        console.error('Error loading tasks:', error)
        return
      }

      setTasks(data || [])
    } catch (error) {
      console.error('Error loading tasks:', error)
    } finally {
      setLoading(false)
    }
  }

  const addTask = async () => {
    if (!newTask.trim()) return

    const selectedUser = teamLeaders.find(tl => tl.id === assignedTo)

    try {
      const { error } = await supabase
        .from('tasks')
        .insert([{
          task: newTask,
          completed: false,
          scheduled_at: scheduledTime ? new Date(`${new Date().toDateString()} ${scheduledTime}`).toISOString() : null,
          created_by: user?.id,
          created_by_name: user?.name || 'Admin',
          assigned_to: assignedTo || null,
          assigned_to_name: selectedUser?.name || null,
          priority: priority
        }])

      if (error) {
        console.error('Error adding task:', error)
        alert('Fout bij toevoegen taak: ' + error.message)
        return
      }

      // Reset form
      setNewTask('')
      setAssignedTo('')
      setScheduledTime('')
      setPriority('normaal')
      setShowAddForm(false)
      loadTasks()
    } catch (error) {
      console.error('Error adding task:', error)
    }
  }

  const toggleTaskComplete = async (taskId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('tasks')
        .update({
          completed: !currentStatus,
          completed_at: !currentStatus ? new Date().toISOString() : null
        })
        .eq('id', taskId)

      if (error) {
        console.error('Error updating task:', error)
        return
      }

      loadTasks()
    } catch (error) {
      console.error('Error updating task:', error)
    }
  }

  const deleteTask = async (taskId: string) => {
    if (!confirm('Weet je zeker dat je deze taak wilt verwijderen?')) return

    try {
      const { error } = await supabase
        .from('tasks')
        .delete()
        .eq('id', taskId)

      if (error) {
        console.error('Error deleting task:', error)
        return
      }

      loadTasks()
    } catch (error) {
      console.error('Error deleting task:', error)
    }
  }

  const toggleExpanded = (taskId: string) => {
    const newExpanded = new Set(expandedTasks)
    if (newExpanded.has(taskId)) {
      newExpanded.delete(taskId)
    } else {
      newExpanded.add(taskId)
    }
    setExpandedTasks(newExpanded)
  }

  // Filter tasks
  const filteredTasks = tasks.filter(task => {
    if (filterUser && task.assigned_to !== filterUser) return false
    if (filterStatus === 'completed' && !task.completed) return false
    if (filterStatus === 'pending' && task.completed) return false
    return true
  })

  // Group tasks by assigned user
  const groupedTasks = filteredTasks.reduce((acc, task) => {
    const key = task.assigned_to_name || 'Niet toegewezen'
    if (!acc[key]) acc[key] = []
    acc[key].push(task)
    return acc
  }, {} as Record<string, Task[]>)

  const getPriorityColor = (priority?: string) => {
    switch (priority) {
      case 'hoog': return 'text-red-600 bg-red-50 dark:bg-red-900/30'
      case 'laag': return 'text-gray-500 bg-gray-50 dark:bg-gray-600'
      default: return 'text-yellow-600 bg-yellow-50 dark:bg-yellow-900/30'
    }
  }

  const getPriorityLabel = (priority?: string) => {
    switch (priority) {
      case 'hoog': return 'Hoog'
      case 'laag': return 'Laag'
      default: return 'Normaal'
    }
  }

  // Stats
  const totalTasks = tasks.length
  const completedTasks = tasks.filter(t => t.completed).length
  const pendingTasks = totalTasks - completedTasks
  const highPriorityPending = tasks.filter(t => !t.completed && t.priority === 'hoog').length

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-800 dark:text-white">Takenbeheer</h2>
        <div className="flex gap-2">
          <button
            onClick={loadTasks}
            className="p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors"
            title="Ververs"
          >
            <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="flex items-center gap-2 bg-red-800 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
          >
            <Plus size={18} />
            Nieuwe Taak
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-gray-50 dark:bg-gray-600 p-3 rounded-lg text-center">
          <div className="text-2xl font-bold text-gray-800 dark:text-white">{totalTasks}</div>
          <div className="text-xs text-gray-500 dark:text-gray-300">Totaal</div>
        </div>
        <div className="bg-green-50 dark:bg-green-900/30 p-3 rounded-lg text-center">
          <div className="text-2xl font-bold text-green-600">{completedTasks}</div>
          <div className="text-xs text-green-600">Voltooid</div>
        </div>
        <div className="bg-yellow-50 dark:bg-yellow-900/30 p-3 rounded-lg text-center">
          <div className="text-2xl font-bold text-yellow-600">{pendingTasks}</div>
          <div className="text-xs text-yellow-600">Open</div>
        </div>
        <div className="bg-red-50 dark:bg-red-900/30 p-3 rounded-lg text-center">
          <div className="text-2xl font-bold text-red-600">{highPriorityPending}</div>
          <div className="text-xs text-red-600">Urgent</div>
        </div>
      </div>

      {/* Add Task Form */}
      {showAddForm && (
        <div className="bg-gray-50 dark:bg-gray-600 p-4 rounded-lg border border-gray-200 dark:border-gray-500">
          <h3 className="font-semibold text-gray-800 dark:text-white mb-3">Nieuwe taak toevoegen</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Taak omschrijving *
              </label>
              <input
                type="text"
                value={newTask}
                onChange={(e) => setNewTask(e.target.value)}
                placeholder="Beschrijf de taak..."
                className="w-full p-2 border border-gray-300 dark:border-gray-500 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Toewijzen aan
              </label>
              <select
                value={assignedTo}
                onChange={(e) => setAssignedTo(e.target.value)}
                className="w-full p-2 border border-gray-300 dark:border-gray-500 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-red-500"
              >
                <option value="">-- Selecteer medewerker --</option>
                {teamLeaders.map(tl => (
                  <option key={tl.id} value={tl.id}>
                    {tl.name} ({tl.role})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Prioriteit
              </label>
              <div className="flex rounded-lg overflow-hidden border border-gray-300 dark:border-gray-500">
                {([
                  { val: 'laag', label: 'Laag', activeClass: 'bg-gray-500 text-white' },
                  { val: 'normaal', label: 'Normaal', activeClass: 'bg-yellow-500 text-white' },
                  { val: 'hoog', label: 'Hoog', activeClass: 'bg-red-600 text-white' },
                ] as const).map(opt => (
                  <button
                    key={opt.val}
                    type="button"
                    onClick={() => setPriority(opt.val)}
                    className={`flex-1 py-2 text-sm font-medium transition-colors ${
                      priority === opt.val
                        ? opt.activeClass
                        : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Gepland tijdstip (optioneel)
              </label>
              <input
                type="time"
                value={scheduledTime}
                onChange={(e) => setScheduledTime(e.target.value)}
                className="w-full p-2 border border-gray-300 dark:border-gray-500 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-red-500"
              />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button
              onClick={() => {
                setShowAddForm(false)
                setNewTask('')
                setAssignedTo('')
                setScheduledTime('')
                setPriority('normaal')
              }}
              className="px-4 py-2 bg-gray-300 dark:bg-gray-500 text-gray-700 dark:text-white rounded-lg hover:bg-gray-400 dark:hover:bg-gray-400 transition-colors"
            >
              Annuleren
            </button>
            <button
              onClick={addTask}
              disabled={!newTask.trim()}
              className="px-4 py-2 bg-red-800 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              Taak Toevoegen
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
        <select
          value={filterUser}
          onChange={(e) => setFilterUser(e.target.value)}
          className="p-2 min-h-touch border border-gray-300 dark:border-gray-500 dark:bg-gray-600 dark:text-white rounded-lg text-sm w-full sm:w-auto"
        >
          <option value="">Alle medewerkers</option>
          {teamLeaders.map(tl => (
            <option key={tl.id} value={tl.id}>{tl.name}</option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="p-2 min-h-touch border border-gray-300 dark:border-gray-500 dark:bg-gray-600 dark:text-white rounded-lg text-sm w-full sm:w-auto"
        >
          <option value="">Alle statussen</option>
          <option value="pending">Open</option>
          <option value="completed">Voltooid</option>
        </select>
      </div>

      {/* Tasks List - Grouped by User */}
      {loading ? (
        <div className="flex justify-center py-8">
          <RefreshCw className="animate-spin text-gray-400" size={32} />
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(groupedTasks).map(([userName, userTasks]) => (
            <div key={userName} className="border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
              {/* User Header */}
              <div className="bg-gray-100 dark:bg-gray-600 px-4 py-2 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <User size={18} className="text-gray-600 dark:text-gray-300" />
                  <span className="font-semibold text-gray-800 dark:text-white">{userName}</span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    ({userTasks.filter(t => !t.completed).length} open)
                  </span>
                </div>
              </div>

              {/* Tasks */}
              <div className="divide-y divide-gray-100 dark:divide-gray-600">
                {userTasks.map(task => (
                  <div
                    key={task.id}
                    className={`p-3 ${task.completed ? 'bg-green-50/50 dark:bg-green-900/10' : 'bg-white dark:bg-gray-700'}`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Checkbox */}
                      <button
                        onClick={() => toggleTaskComplete(task.id, task.completed)}
                        className={`mt-0.5 ${task.completed ? 'text-green-500' : 'text-gray-400 hover:text-green-500'}`}
                      >
                        {task.completed ? <CheckCircle size={22} /> : <Circle size={22} />}
                      </button>

                      {/* Task Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`${task.completed ? 'line-through text-gray-400' : 'text-gray-800 dark:text-white'}`}>
                            {task.task}
                          </span>
                          {task.priority && (
                            <span className={`text-xs px-2 py-0.5 rounded-full ${getPriorityColor(task.priority)}`}>
                              {getPriorityLabel(task.priority)}
                            </span>
                          )}
                        </div>

                        {/* Meta info */}
                        <div className="flex items-center gap-4 mt-1 text-xs text-gray-500 dark:text-gray-400">
                          {task.scheduled_at && (
                            <span className="flex items-center gap-1">
                              <Clock size={12} />
                              {new Date(task.scheduled_at).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          )}
                          <span>
                            Door: {task.created_by_name}
                          </span>
                          <span>
                            {new Date(task.created_at).toLocaleDateString('nl-NL')}
                          </span>
                          {task.completed && task.completed_at && (
                            <span className="text-green-600">
                              Voltooid: {new Date(task.completed_at).toLocaleString('nl-NL')}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Delete Button */}
                      <button
                        onClick={() => deleteTask(task.id)}
                        className="text-gray-400 hover:text-red-500 transition-colors"
                        title="Verwijderen"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {filteredTasks.length === 0 && (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <AlertCircle size={48} className="mx-auto mb-2 opacity-50" />
              <p>Geen taken gevonden</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
